import React, { useContext } from 'react'

import { Link } from 'react-router-dom'

import { Grid, GridSize, makeAppStyles } from '@percept/mui'

import { vodafoneExternalLinks } from 'components/Links'

import { UserPrivilegeContext } from '@percept/app-components'

import { userHasAnyOrgPrivileges, userHasGlobalPrivileges, userHasOrgPrivileges } from '@percept/utils'

import { GlobalPrivilege, OrgPrivilege, OrgPrivileges } from '@percept/types'
import { VODAFONE_GLOBAL_ID } from 'vodafoneMarkets'
import { every } from 'lodash-es'


type PrivilegedLinkBase = {
  name: string
  globalPrivileges?: GlobalPrivilege[] | null
  path?: string
  openInNewWindow?: boolean
}
type PrivilegedLink = PrivilegedLinkBase & (
  {
    orgUnitId?: never
    orgPrivileges?: never
  } | {
    orgUnitId: string
    orgPrivileges: OrgPrivilege[]
  }
)

type LinkSize = 'default' | 'small'

type LinkHierarchy = PrivilegedLink & {
  children?: LinkHierarchy[]
  size?: LinkSize
}


const filterLinkHierarchy = (
  link: LinkHierarchy,
  global_privileges: GlobalPrivilege[],
  org_privileges: OrgPrivileges[],
): LinkHierarchy | null => {
  const flags: boolean[] = []
  if( link.globalPrivileges ){
    flags.push(
      userHasGlobalPrivileges(link.globalPrivileges, global_privileges)
    )
  }
  if( link.orgPrivileges ){
    if( link.orgUnitId === '*' ){
      flags.push(userHasAnyOrgPrivileges(link.orgPrivileges, org_privileges))
    }else{
      flags.push(userHasOrgPrivileges(link.orgPrivileges, link.orgUnitId, org_privileges))
    }
  }
  if( flags.length && !every(flags) ) return null
  if( link.children ){
    const filteredChildren = link.children
      .map( c => filterLinkHierarchy(c, global_privileges, org_privileges) )
      .filter( (c): c is LinkHierarchy => c !== null )
    if( !filteredChildren.length ) return null
    return { ...link, children: filteredChildren }
  }
  return link
}


const useLinkHierarchy = (): LinkHierarchy[] => {
  const {
    global_privileges = [],
    org_privileges = [],
    user: { default_org_unit_id } = { default_org_unit_id: null }
  } = useContext(UserPrivilegeContext)

  const links: LinkHierarchy[] = [
    {
      name: 'Strategy',
      path: '/wizard/strategy',
      children: [
        {
          name: 'Pathways',
          path: '/wizard/strategy/pathways',
          openInNewWindow: true,
          globalPrivileges: ['vfMicrosites.pathways.view'],
        },
        // {
        //   name: 'Brand Growth Planner',
        //   path: '/wizard/strategy/brand-growth-planner',
        //   openInNewWindow: true,
        //   globalPrivileges: ['brandGrowthPlanner.viewAny'],
        // },
        {
          name: 'Media Mix Modelling',
          path: '/wizard/strategy/media-mix-modelling',
          openInNewWindow: true,
          globalPrivileges: ['vfMicrosites.mmm.view'],
        },
        {
          name: 'Representation',
          path: vodafoneExternalLinks.representation,
          openInNewWindow: true,
        },
      ]
    },
    {
      name: 'Optimisation',
      path: '/wizard/optimisation',
      children: [
        {
          name: 'Media Quality',
          path: default_org_unit_id ? `/dashboards/${default_org_unit_id}` : undefined,
          openInNewWindow: true,
          globalPrivileges: ['structuralReporting.viewAny', 'performanceOverview.viewAny'],
        },
        /* NOTE- Creative Quality Dashboard has been replaced with Total Discovery Dashboard */
        // {
        //   name: 'Creative Quality',
        //   path: vodafoneExternalLinks.creativeX,
        //   openInNewWindow: true,
        // },
        {
          name: 'Total Discovery',
          children: vodafoneExternalLinks.discoveryDashboardsByMarket.map( ({ market, url }) => ({
            name: market,
            path: url,
            size: 'small',
            openInNewWindow: true,
          })),
        },
        {
          name: 'Media Carbon Calculator',
          path: vodafoneExternalLinks.carbonCalculator,
          openInNewWindow: true,
        },
        /* NOTE - Partnerships application has been disabled */
        // {
        //   name: 'Partnerships',
        //   path: '/wizard/optimisation/partnerships',
        //   openInNewWindow: true,
        //   globalPrivileges: ['partnerships.viewAny'],
        // },
        {
          name: 'Sponsorships',
          path: '/wizard/optimisation/sponsorships',
          openInNewWindow: true,
          globalPrivileges: ['sponsorships.viewAny'],
        },
        {
          name: 'Media Academy',
          path: '/wizard/optimisation/media-academy',
          openInNewWindow: true,
          orgUnitId: VODAFONE_GLOBAL_ID,
          orgPrivileges: ['vfMediaWizard.mediaAcademy.view'],
        },
      ]
    },
    {
      name: 'Reporting',
      path: '/wizard/reporting',
      children: [
        {
          name: 'Media Investment',
          path: default_org_unit_id ? `/reporting/${default_org_unit_id}` : undefined,
          openInNewWindow: true,
          globalPrivileges: ['mediaInvestment.viewAny'],
        },
        {
          name: 'Smart Campaign Assessment',
          path: '/wizard/reporting/smart-campaign-assessment',
          openInNewWindow: true,
          globalPrivileges: ['vfSmartCampaignAssessment.view'],
        },
        {
          name: 'Wastage Trend Reports',
          path: '/wizard/reporting/wastage-trend-reports',
          openInNewWindow: true,
          globalPrivileges: ['vfWastageTrendReports.viewAny'],
        },
        ...( default_org_unit_id ? [
          {
            name: 'Digital Benchmarking',
            path: '/wizard/reporting/benchmarking',
            openInNewWindow: true,
            orgPrivileges: ['benchmarking.view'],
            orgUnitId: '*',
          }
        ] : []) as PrivilegedLink[],
        // {
        //   name: 'KPIs',
        // },
        // {
        //   name: 'Brand Consistency',
        // },
      ]
    },
    {
      name: 'News',
      path: '/wizard/news',
      children: [
        {
          name: 'News',
        },
      ]
    }
  ]

  return links
    .map( link => filterLinkHierarchy(link, global_privileges || [], org_privileges) )
    .filter( (l): l is LinkHierarchy => l !== null )
}


const useStyles = makeAppStyles( theme => ({
  root: {
    marginBottom: theme.spacing(3),
  },
  gridItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  page: {
    color: theme.palette.action.disabled,
    width: 'max-content',
    fontWeight: 700,
    fontSize: 20,
    lineHeight: '26px',
    marginBottom: theme.spacing(2),
  },
  childPage: {
    color: theme.palette.action.disabled,
    width: 'max-content',
    fontSize: 18,
    lineHeight: '26px',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  nested: {
    marginLeft: theme.spacing(3),
  },
  link: {
    color: 'inherit',
    textDecoration: 'underline solid transparent',
    textUnderlineOffset: '5px',
    '&:hover': {
      color: theme.palette.primary.main,
      textDecoration: `underline solid ${theme.palette.primary.main}`,
    },
    transition: theme.transitions.create(['color', 'text-decoration']),
  },
  small: {
    fontSize: 14,
    lineHeight: '18px',
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
}))


type LinkNodeProps = {
  link: LinkHierarchy
  depth: number
  parentSize?: LinkSize
  classes: ReturnType<typeof useStyles>
}

const LinkNode = ({ link, depth, parentSize = 'default', classes }: LinkNodeProps): JSX.Element => {
  const { name, path, openInNewWindow, children } = link
  const size = link.size ?? parentSize
  const baseClassName = depth === 0 ? classes.page : classes.childPage
  const sizeClassName = size === 'small' ? ` ${classes.small}` : ''
  const nestedClassName = depth > 1 ? ` ${classes.nested}` : ''
  const labelClassName = `${baseClassName}${sizeClassName}${nestedClassName}`
  const linkClassName = `${labelClassName} ${classes.link}`

  let label: JSX.Element
  if( path && openInNewWindow ){
    label = (
      <a
        className={linkClassName}
        href={path}
        target='_blank'
        rel='noopener noreferrer'>
        { name }
      </a>
    )
  }else if( path ){
    label = (
      <Link to={path} className={linkClassName}>{ name }</Link>
    )
  }else{
    label = <span className={labelClassName}>{ name }</span>
  }

  return (
    <>
      { label }
      { children && children.map( child => (
        <LinkNode key={child.name} link={child} depth={depth + 1} parentSize={size} classes={classes} />
      ))}
    </>
  )
}


export const FooterLinks = (): JSX.Element => {
  const classes = useStyles()
  const linkHierarchy = useLinkHierarchy()
  let gridSize: GridSize = 12
  switch(linkHierarchy.length){
    case 4:
      gridSize = 3
      break
    case 3:
      gridSize = 4
      break
    case 2:
      gridSize = 6
      break
    case 1:
    default:
      gridSize = 12
  }
  return (
    <Grid className={classes.root} container spacing={4} justify='space-evenly'>
      { linkHierarchy.map( link => (
        <Grid key={link.name} className={classes.gridItem} item xs={12} sm={gridSize}>
          <LinkNode link={link} depth={0} classes={classes} />
        </Grid>
      ))}
    </Grid>
  )
}
